import React from 'react'
import { I18n } from '@front/volcanion'
import { useTheme } from '@mui/material/styles'


import { Typography, Box, IconButton, Grid, StepLabel, StepContent, Icon } from '@mui/material'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'

import { SingleOptionSelectorField, TextField, AddressAutocompleteField } from '@front/squirtle'


const Row1 = ({
  prefix,
  owner,
  favorite_address,
  handleSetFavoriteAddress,
  onAddressResolve,
  disabledIconButton,
  isB2B,
  commercial_formula,
  schedule_type
}) =>
  <Grid container spacing={1} size={12}>
    <Grid size="grow">
      <Box sx={{ display: 'flex' }}>
        <IconButton onClick={handleSetFavoriteAddress} disabled={disabledIconButton}>
          <Box color={!!favorite_address || !disabledIconButton ? 'pink' : 'text.label'}>
            {favorite_address ? <Icon >favorite</Icon> : <Icon >favorite_border</Icon>}
          </Box>
        </IconButton>
        <AddressAutocompleteField
          name={prefix}
          search_id={prefix === 'source' ? 'address_source' : 'address_destination'}
          label={I18n.t(`address.${prefix}`)}
          noOptionsText={I18n.t('no_result')}
          owner={owner}
          initialConfig={{
            includePOI: true,
            load_type: 'user',
            requestOptions: {
              recentLimit: 4,
              favoriteLimit: 4,
              commercial_formula,
              direction_type: prefix,
              schedule_type
            },
            watchers: [owner, isB2B, commercial_formula, schedule_type, prefix]
          }}
          required={prefix === 'source'}
          resolveAddress
          onAddressResolve={onAddressResolve}
          getOptionIcon={(option) => {
            if (_.get(option, 'favorite')) return 'favorite'
            if (_.get(option, 'type') === 'home') return 'home'
            else if (_.get(option, 'type') === 'work') return 'work'
            else if (_.get(option, 'type') === 'airport') return 'flighttakeoff'
            else if (_.get(option, 'type') === 'festival') return 'festival'
            else if (_.get(option, 'type') === 'train') return 'train'
            else if (_.get(option, 'address_id')) return 'update'
          }}
        />
      </Box>
    </Grid>
  </Grid>

const Row2 = ({ companyuser_id, vehicle_count, isB2B, guest_booking, guest, handleGuestChange, prefix, prefix_field }) => {
  const theme = useTheme()
  return (
    <Grid container spacing={1} size={12}>
      <Grid hidden={prefix !== 'source'} size="grow">
        <TextField
          name={`loads.${prefix_field}_contact`}
          label={I18n.t('transport.passenger')}
          disabled={!!isB2B && ((!guest && !!companyuser_id) || vehicle_count > 1)}
          required={vehicle_count === 1 && prefix !== 'destination'}
          alwaysInclude
        />
      </Grid>
      <Grid hidden={prefix !== 'source'} size="grow">
        <TextField
          name={`loads.${prefix_field}_phone`}
          label={I18n.t('phone.label', { count: 1 })}
          disabled={!!isB2B && ((!guest && !!companyuser_id) || vehicle_count > 1)}
          alwaysInclude
          required={prefix !== 'destination'}
        />
      </Grid>
      <Grid hidden={!isB2B || prefix !== 'source'} size="grow">
        <SingleOptionSelectorField
          name={'guest'}
          optionLabel={I18n.t('order.guest')}
          disabled={!guest_booking}
          afterChange={handleGuestChange}
          getOptionLabelProps={() => ({
            disableTypography: false,
            slotProps: { typography: { fontSize: 12, color: `${theme.palette.text.label} !important` } }
          })}
        />
      </Grid>
      <Grid hidden={!isB2B || prefix !== 'source'} size="grow">
        <SingleOptionSelectorField
          name={'fill_by_driver'}
          optionLabel={I18n.t('fill_by_driver')}
          disabled
          getOptionLabelProps={() => ({
            disableTypography: false,
            slotProps: { typography: { fontSize: 12, color: `${theme.palette.text.label} !important` } }
          })}
        />
      </Grid>
    </Grid>
  );
}

// CA (planned): name + origin always required regardless of arrival/meeting_point.
// CI (immediate): only required when user toggled is_reservationinfo (= arriving from flight/train).
const Row3 = ({ isReadOnly, reservationinfo, additional_info_required, schedule_type, handleIsReservationinfoChange }) => {
  const fields_required = !!additional_info_required || schedule_type === 'planned'
  return (
    <Grid container spacing={1}>
      {!isReadOnly && <Grid size="grow">
        <SingleOptionSelectorField
          name={'is_reservationinfo'}
          label={reservationinfo?.arrival_translated}
          afterChange={handleIsReservationinfoChange}
        />
      </Grid>
      }
      <Grid size="grow">
        <TextField
          name={'reservationinfo.name'}
          label={_.get(reservationinfo, 'name_translated')}
          required={fields_required}
        />
      </Grid>
      <Grid size="grow">
        <TextField
          name={'reservationinfo.origin'}
          label={_.get(reservationinfo, 'origin_translated')}
          required={fields_required}
        />
      </Grid>
    </Grid>
  );
}

const Row4 = ({ handleReplaceComment, disabledIconButton, save_comment, prefix_field }) =>
  <Grid container spacing={1} size={12}>
    <Grid size="grow">
      <Box sx={{ display: 'flex' }}>
        <IconButton onClick={handleReplaceComment} disabled={disabledIconButton}>
          <Box color={!!save_comment || !disabledIconButton ? 'green' : 'text.label'}>
            {!!save_comment ? <Icon >save</Icon> : <SaveOutlinedIcon />}
          </Box>
        </IconButton>
        <TextField
          name={`info.${prefix_field}_comment`}
          label={I18n.t('comment.label', { count: 1 })}
          alwaysInclude
        />
      </Box>
    </Grid>
  </Grid>

const AddressSection = props => {
  const { prefix, display_additional_info, swapAddress, display_swap_button } = props
  return <>
    <StepLabel>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant='h5'>{I18n.t(`address.${prefix}`)}</Typography>
        {!!display_swap_button && (
          <IconButton onClick={swapAddress}>
            <Icon color='primary'>swap_vert</Icon>
          </IconButton>
        )}
      </Box>
    </StepLabel>
    <StepContent>
      <Grid container spacing={1} rowSpacing={1.5}>
        <Row1 {...props} />
        <Row2 {...props} />
        {!!display_additional_info && prefix === 'source' && <Row3 {...props} />}
        <Row4 {...props} />
      </Grid>
    </StepContent>
  </>
}

export default React.memo(AddressSection)
